import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Col,
  Empty,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Typography,
  notification,
} from 'antd'
import { Controller, useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import Editor from '../../../shared/components/common/editor'
import { generateValidationSchema } from '../utils'
import HelpHeader from './help-header'
import '../index.scss'
import { useNavigate, useParams } from 'react-router-dom'
import { menuUrls } from '../../../shared/constants/menu.constants'
import SchedulePublishingModal, { ISchedulePublishing, State } from './SchedulePublishingModal'
import {
  IHelpPageDetail,
  IReqCheckPageExist,
  IReqCreateHelpPage,
  IReqUpdateHelpPage,
  useCheckPageExist,
  useCreateHelpPage,
  useDetailHelpPage,
  usePageSetup,
  useRestoreHelpPage,
  useUpdateHelpPage,
} from '../useHelpPage'
import { STATUS, VersionStatusTag } from './TagComponents'
import dayjs from 'dayjs'
import { colorBgButtonBold } from '../../../themes/primaryThemeDefault'
import { SYSTEM_MESSAGE_IDS } from '../../../shared/constants/systemMessages.constants'
import { PREVIEW_HELP_PAGE } from '../../../shared/constants/localStorage.constants'
import { random } from 'lodash'
import { FORMAT_DATE } from '../../../shared/constants/app.constants'
import { useRolePermission } from '../../../shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from '../../../shared/models/roleAndPermission.model'
import CustomSelect from 'src/app/shared/components/common/select'

interface IHelpPageFormData {
  pageName: string | null
  url: string | null
  content: string | null
  pageId: string
  parentId?: string
}

export enum FormMode {
  view,
  add,
  edit,
}

interface PropsHelpPageForm {
  title?: string
  version?: {
    nameVersion: string
    tag: string
  }
  mode?: FormMode
}

const HelpPageForm: React.FC<PropsHelpPageForm> = ({ title, version, mode = FormMode.view }) => {
  const { t } = useTranslation(undefined, { useSuspense: true })
  const navigate = useNavigate()
  const schedulePublishingModalRef = useRef<{ openModal: Function }>(null)
  const restoreModalRef = useRef<{ openModal: Function }>(null)
  const [api, contextHolder] = notification.useNotification()

  const defaultValues: IHelpPageFormData = {
    pageName: null,
    url: null,
    content: null,
    pageId: '',
  }

  const params = useParams()
  const { id } = params
  const { pageNameOptions, isLoading: isLoadingPageNameOptions, getPageNameOptions } = usePageSetup()
  const { isLoading, createHelpPage } = useCreateHelpPage()
  const { isLoading: isLoadingCheckPageExit, checkPageExist } = useCheckPageExist()
  const [isLoadingSaveDraft, setIsLoadingSaveDraft] = useState<boolean>(false)

  const { isLoading: isLoadingGetDetail, getHelpPage } = useDetailHelpPage()
  const { isLoading: isLoadingUpdateHelpPage, updateHelpPage } = useUpdateHelpPage()
  const disableAllField: boolean = mode === FormMode.view ? true : false
  const [helpPageDetail, setHelpPageDetail] = useState<IHelpPageDetail>()

  const { isLoading: isLoadingRestore, restoreHelpPage } = useRestoreHelpPage()
  const { userPermissions } = useRolePermission()
  const [content, setContent] = useState<string>('')

  const {
    control,
    formState: { errors, touchedFields, isValid, isDirty },
    getValues,
    setValue,
    setError,
    reset,
  } = useForm<any>({ mode: 'all', defaultValues, resolver: yupResolver(generateValidationSchema(t)) })

  const isSaveButtonDisabled = !isValid || !!Object.keys(errors)?.length || !Object.keys(touchedFields)?.length

  const handleCheckExistPage = async () => {
    const { pageName, url, pageId } = getValues() as IHelpPageFormData

    if (pageName) {
      const reqCheckPageExist: IReqCheckPageExist = {
        mainPageName: pageName,
        url: url as string,
        mainPageId: pageId,
        idEdit: null,
      }

      if (mode === FormMode.edit && id) {
        reqCheckPageExist.idEdit = id
      }

      const resCheckPageExist = await checkPageExist(reqCheckPageExist)

      if (resCheckPageExist.isExist) {
        setError('pageName', {
          message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_EXIST, {
            ALREADY_EXIST: reqCheckPageExist.mainPageName,
          }),
        })
      }
    } else {
      setError('pageName', {
        message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
          Required_Field: `${t('HelpPage:form.label.pageName')}`,
        }),
      })
    }
  }

  const handlePageNameChange = async (value: string, option: { label: string; value: string; id: string }) => {
    setValue('pageName', option.label, {
      shouldTouch: true,
      shouldValidate: true,
    })
    setValue('url', value)
    setValue('pageId', option.id)

    handleCheckExistPage()
  }

  const getDetailHelpPage = async () => {
    if ((mode === FormMode.edit || mode === FormMode.view) && id) {
      const res = await getHelpPage(id)

      if (res) {
        setHelpPageDetail(res)
        setContent(res.content)
        reset({
          pageName: res.mainPageName as string,
          url: res.url,
          content: res.content,
          pageId: res.mainPageId,
          parentId: res.parentId,
        })
      }
    }

    // if (mode === FormMode.edit) {
    //   handleCheckExistPage()
    // }
  }

  const handlePreview = () => {
    const dataPreview = {
      pageName: getValues('pageName'),
      content: getValues('content'),
    }
    localStorage.setItem(PREVIEW_HELP_PAGE, JSON.stringify(dataPreview))
    window.open(`${menuUrls.helpPageManagement}/preview/${random(1, 1000)}`, '_blank', 'noreferrer')
  }

  const handleAddHelpPage = async (data?: ISchedulePublishing, status?: STATUS) => {
    const { pageName, url, content, pageId } = getValues() as IHelpPageFormData
    const isPublishNow: boolean = data?.schedulePublishing ? false : true

    const getStatus = () => {
      if (status === STATUS.DRAFT) {
        return STATUS.DRAFT
      } else {
        return isPublishNow ? STATUS.PUBLISHED : STATUS.SCHEDULED
      }
    }

    const reqData: IReqCreateHelpPage = {
      mainPageId: pageId,
      content: content as string,
      url: url as string,
      mainPageName: pageName as string,
      publishSchedule: data?.schedulePublishing ?? null,
      status: getStatus(),
    }

    const res = await createHelpPage(reqData)

    if (res.isSuccess) {
      let message: string = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PAGE_NAME_PUBLISHED_SUCCESSFULLY, {
        PAGE_NAME_PUBLISHED_SUCCESSFULLY: pageName,
      })

      if (data?.schedulePublishing) {
        message =
          t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PAGE_NAME_SCHEDULED_SUCCESSFULLY, {
            PAGE_NAME_SCHEDULED_SUCCESSFULLY: pageName,
          }) + ` ${dayjs(data.schedulePublishing).format(FORMAT_DATE)}`
      }

      if (status === STATUS.DRAFT) {
        message = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.SAVE_DRAFT_SUCCESSFULLY, {
          Page_Name: pageName,
        })
      }

      navigate(menuUrls.helpPageManagement, {
        state: {
          helpPageManagement: {
            type: 'success',
            message: message,
          },
        },
      })
    } else {
      api.error({
        message: t('common:error'),
        description: `${res.error}`,
        placement: 'topRight',
      })
    }
  }

  const handleEditHelpPage = async (data?: ISchedulePublishing, status?: STATUS) => {
    const { pageName, url, content, pageId, parentId } = getValues() as IHelpPageFormData
    const isPublishNow: boolean = data?.schedulePublishing ? false : true

    const getStatus = () => {
      if (status === STATUS.DRAFT) {
        return STATUS.DRAFT
      } else {
        return isPublishNow ? STATUS.PUBLISHED : STATUS.SCHEDULED
      }
    }

    const reqData: IReqUpdateHelpPage = {
      parentId: parentId as any,
      mainPageId: pageId,
      content: content as string,
      url: url as string,
      mainPageName: pageName as string,
      publishSchedule: data?.schedulePublishing ?? null,
      status: getStatus(),
    }

    const res = await updateHelpPage(id as string, reqData)

    if (res.isSuccess) {
      let message: string = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PAGE_NAME_PUBLISHED_SUCCESSFULLY, {
        PAGE_NAME_PUBLISHED_SUCCESSFULLY: pageName,
      })

      if (data?.schedulePublishing) {
        message =
          t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PAGE_NAME_SCHEDULED_SUCCESSFULLY, {
            PAGE_NAME_SCHEDULED_SUCCESSFULLY: pageName,
          }) + ` ${dayjs(data.schedulePublishing).format(FORMAT_DATE)}`
      }

      if (status === STATUS.DRAFT) {
        message = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.SAVE_DRAFT_SUCCESSFULLY, {
          Page_Name: pageName,
        })
      }

      navigate(menuUrls.helpPageManagement, {
        state: {
          helpPageManagement: {
            type: 'success',
            message: message,
          },
        },
      })
    } else {
      api.error({
        message: t('common:error'),
        description: `${res.error}`,
        placement: 'topRight',
      })
    }
  }

  const handleSaveDraft = async () => {
    try {
      setIsLoadingSaveDraft(true)
      if (mode === FormMode.add) {
        await handleAddHelpPage(undefined, STATUS.DRAFT)
      }

      if (mode === FormMode.edit) {
        await handleEditHelpPage(undefined, STATUS.DRAFT)
      }
    } catch (e) {
      console.log('ERROR', e)
    } finally {
      setIsLoadingSaveDraft(false)
    }
  }

  const handlePublish = async () => {
    if (schedulePublishingModalRef.current) {
      schedulePublishingModalRef.current.openModal()
    }
  }

  const handleSubmitForm = (data?: ISchedulePublishing, status?: STATUS) => {
    if (mode === FormMode.add) {
      handleAddHelpPage(data, status)
    }

    if (mode === FormMode.edit) {
      handleEditHelpPage(data, status)
    }
  }

  const handleRestore = async (data?: ISchedulePublishing) => {
    if (id) {
      if (data) {
        const res = await restoreHelpPage({
          id: id as string,
          publishSchedule: data?.schedulePublishing ?? null,
          status: data?.schedulePublishing ? STATUS.SCHEDULED : STATUS.PUBLISHED,
        })

        if (res.isSuccess) {
          let message: string = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.RESTORE_VERSION_SUCCESSFULLY, {
            RESTORE_VERSION_SUCCESSFULLY: `v.${helpPageDetail?.version}`,
          })

          navigate(`${menuUrls.helpPageManagement}/view-version-history/${res.value.parentId}`, {
            state: {
              versionHistory: {
                type: 'success',
                message: message,
              },
            },
          })
        }
      } else {
        const res = await restoreHelpPage({
          id: id as string,
          publishSchedule: '',
          status: helpPageDetail?.status as number,
        })

        if (res.isSuccess) {
          let message: string = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.RESTORE_VERSION_SUCCESSFULLY, {
            RESTORE_VERSION_SUCCESSFULLY: `v.${helpPageDetail?.version}`,
          })

          navigate(`${menuUrls.helpPageManagement}/view-version-history/${res.value.parentId}`, {
            state: {
              versionHistory: {
                type: 'success',
                message: message,
              },
            },
          })
        }
      }
    }
  }

  const getDefaultValueSchedulePublishing = useMemo(() => {
    if (helpPageDetail) {
      return {
        schedulePublishing: null,
        publishing: State.publishNow,
      }
    }
    return undefined
  }, [helpPageDetail])

  const handleGoBack = () => {
    if (mode === FormMode.view) {
      navigate(-1)
    } else {
      navigate(menuUrls.helpPageManagement)
    }
  }

  const handleBlurPageName = () => {
    if (errors.pageName) {
      setError('pageName', errors.pageName)
    }

    if (!getValues('pageName')) {
      const messageError = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
        Required_Field: `${t('HelpPage:form.label.pageName')}`,
      })
      setError('pageName', { message: messageError })
    }
  }

  const handleEditorChange = (value: string) => {
    setValue('content', value, { shouldValidate: true, shouldTouch: true })
    setContent(value)
  }

  const handleClickRestoreBtn = () => {
    if (helpPageDetail?.status === STATUS.SCHEDULED) {
      if (restoreModalRef.current) {
        restoreModalRef.current.openModal()
      }
    } else {
      handleRestore()
    }
  }

  useEffect(() => {
    getPageNameOptions()
    getDetailHelpPage()
  }, [])
  const isLoadingPageName = isLoadingPageNameOptions || isLoadingCheckPageExit

  return (
    <div className={'help-page-form'}>
      {contextHolder}
      <HelpHeader title={title} onGoBack={handleGoBack} />

      <Form
        name='help-page-form'
        className='form-container'
        layout='vertical'
        style={{ display: isLoadingGetDetail ? 'none' : 'block', paddingBottom: 0 }}
      >
        {mode === FormMode.view && (
          <Flex style={{ marginBottom: 24 }}>
            <Space size={24}>
              <Typography.Text style={{ fontWeight: 600, fontSize: 24 }}>
                {t('HelpPage:form.label.version')} {helpPageDetail?.version}
              </Typography.Text>
              <VersionStatusTag status={helpPageDetail?.status as any} style={{ height: 32 }} />
            </Space>
          </Flex>
        )}

        <Flex vertical>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label={t('HelpPage:form.label.pageName')} className='isRequired'>
                <Controller
                  name='pageName'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Select
                      {...field}
                      filterOption={(input: string, option?: any) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      filterSort={(a, b) => a.label.localeCompare(b.label)}
                      loading={isLoadingPageNameOptions || isLoadingCheckPageExit}
                      showSearch
                      optionFilterProp='children'
                      placeholder={t('HelpPage:form.placeholder.pageName')}
                      onChange={handlePageNameChange as any}
                      onBlur={handleBlurPageName}
                      options={pageNameOptions}
                      disabled={disableAllField || mode === FormMode.edit}
                      notFoundContent={
                        isLoadingPageName ? (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'center',
                            }}
                          >
                            <Spin />
                          </div>
                        ) : (
                          <Empty />
                        )
                      }
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='pageName' />
                </Typography>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={t('HelpPage:form.label.url')} className='isRequired'>
                <Controller name='url' control={control} render={({ field }) => <Input {...field} disabled />} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={t('HelpPage:form.label.content')}
                className={`isRequired editor-help-page ${mode === FormMode.view && 'editor-help-page__disabled'}`}
              >
                <Controller
                  name='content'
                  control={control}
                  render={({ field }) => (
                    <Editor
                      onModelChange={handleEditorChange}
                      model={content}
                      customConfig={{
                        placeholderText: t('HelpPage:form.placeholder.content'),
                        heightMin: 410,
                        heightMax: 650,
                        fontFamilyDefaultSelection: 'Myriad Pro',
                        toolbarSticky: true,
                      }}
                      customEvents={{
                        initialized() {
                          const editor: any = this
                          if (mode === FormMode.view) {
                            editor.edit.off()
                          }
                        },
                      }}
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='content' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>
        </Flex>

        {mode !== FormMode.view && (
          <Flex className='button-group' justify='space-between' gap={24}>
            <Button className='cancel-button' onClick={() => navigate(`${menuUrls.helpPageManagement}`)}>
              {t('OverallFunctionality:cancel_btn')}
            </Button>

            <Space size={24}>
              <Button
                size={'large'}
                className='cancel-button'
                type='default'
                disabled={isSaveButtonDisabled}
                onClick={handlePreview}
              >
                {t('HelpPage:form.button.preview')}
              </Button>

              <Button
                className={isSaveButtonDisabled ? 'help-page-form__save-draft-disable' : ''}
                size={'large'}
                type='default'
                disabled={isSaveButtonDisabled}
                ghost
                style={{ width: 156, fontWeight: 700, color: `${colorBgButtonBold} !important` }}
                onClick={() => handleSaveDraft()}
                loading={isLoadingSaveDraft}
              >
                {t('HelpPage:form.button.saveDraft')}
              </Button>

              <Button className='save-button' type='primary' disabled={isSaveButtonDisabled} onClick={handlePublish}>
                {t('HelpPage:form.button.publish')}
              </Button>
            </Space>
          </Flex>
        )}

        {mode === FormMode.view &&
          userPermissions[ESubModules.HELP_PAGE_MANAGE]?.includes(EPermissions.ADD_EDIT_ITEMS) && (
            <Flex className='button-group' justify='flex-end' gap={24}>
              <Space size={24}>
                <Button
                  size={'large'}
                  className='cancel-button'
                  type='default'
                  onClick={() => {
                    navigate(-1)
                  }}
                >
                  {t('HelpPage:form.button.back')}
                </Button>

                {!helpPageDetail?.isCurrent && (
                  <Button
                    className='save-button'
                    type='primary'
                    loading={helpPageDetail?.status !== STATUS.SCHEDULED && isLoadingRestore}
                    onClick={handleClickRestoreBtn}
                  >
                    {t('HelpPage:form.button.restore')}
                  </Button>
                )}
              </Space>
            </Flex>
          )}
      </Form>

      {isLoadingGetDetail && <Skeleton />}

      <SchedulePublishingModal
        onConfirm={handleSubmitForm}
        ref={schedulePublishingModalRef}
        confirmLoading={isLoading || isLoadingUpdateHelpPage}
        defaultValueProps={getDefaultValueSchedulePublishing}
      />

      <SchedulePublishingModal
        onConfirm={handleRestore}
        ref={restoreModalRef}
        confirmLoading={isLoadingRestore}
        defaultValueProps={getDefaultValueSchedulePublishing}
      />
    </div>
  )
}

export default HelpPageForm
